import { useRef } from 'react';
import type { TenantDetail } from '@order-pilot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SectionCard } from '@/components/SectionCard';
import { deleteTenantLogo, tenantKeys, uploadTenantLogo } from '@/features/tenants/api';
import { API_URL, getErrorMessage } from '@/lib/api';

const ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * Its own section, not a field in "Edit tenant" — replace/remove are
 * immediate, direct actions (no ConfirmDialog): unlike deactivating a
 * tenant, a logo change is fully reversible and locks nobody out, so it
 * doesn't warrant an "are you sure".
 */
export function TenantLogoSection({ tenant }: { tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: tenantKeys.all });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadTenantLogo(tenant.id, file),
    onSuccess: () => void invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTenantLogo(tenant.id),
    onSuccess: () => void invalidate(),
  });

  const pending = uploadMutation.isPending || deleteMutation.isPending;
  const error = uploadMutation.isError
    ? uploadMutation.error
    : deleteMutation.isError
      ? deleteMutation.error
      : undefined;

  const handleFile = (file: File | undefined) => {
    // No client-side size/type pre-check: the server already validates both
    // and returns a clear message through the same error path below — an
    // infrequent, admin-only upload doesn't need a second copy of that logic
    // duplicated client-side just to save one round trip.
    if (file) uploadMutation.mutate(file);
  };

  return (
    <SectionCard headingId="tenant-logo" title="Logo" description="Shown in this restaurant's portal sidebar.">
      <div className="flex items-center gap-5 px-5 py-4">
        <span
          aria-hidden="true"
          className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400"
        >
          {tenant.logoUrl ? (
            <img src={`${API_URL}${tenant.logoUrl}`} alt="" className="h-full w-full object-cover" />
          ) : (
            'No logo'
          )}
        </span>
        <div className="flex flex-col gap-2">
          {error ? <Alert tone="error">{getErrorMessage(error)}</Alert> : null}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={pending}>
              {tenant.logoUrl ? 'Replace' : 'Upload'}
            </Button>
            {tenant.logoUrl ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => deleteMutation.mutate()}
                disabled={pending}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <p className="hint">PNG, JPEG or WebP, up to 2MB.</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          disabled={pending}
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </div>
    </SectionCard>
  );
}
